import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Chevron,
  Container,
  FieldsContainer,
  ContainerCollapsable,
  ContainerTitle,
  ContainerTitleChevron,
  HelperLine,
  VerticalLine,
} from "./styles";
import { messages } from "./messages";
import { DropdownFilter } from "./dropdownFilter";
import { DropdownSort } from "./dropdownSort";
import { InventoryLineSearchBox } from "./inventoryLineSearchBox";
import { ClearAllButton } from "./clearAllButton";
import { FilterButton } from "./filterButton";
import { ShowOnlyMasterLinesCheck } from "./showOnlyMasterLinesCheck";
import { ValueTypes } from "../shared/types";
import { useSortAndFilter } from "./contexts/SortAndFilterContext";

interface SortAndFilterProps {
  roomTypesOptions: { label: string, value: string, isChecked?: boolean }[];
  ratesOptions: { label: string, value: string, isChecked?: boolean }[];
  mealPlansOptions: { label: string, value: string, isChecked?: boolean }[];
  occupanciesOptions: { label: string, value: string, isChecked?: boolean }[];
}

export const SortAndFilter = (props: SortAndFilterProps): JSX.Element => {
  const intl = useIntl();
  const sortAndFilter = useSortAndFilter();
  const [toggleClosed, setToggleClosed] = useState(false);
  const filters = useMemo(() => sortAndFilter?.getFilters(), [sortAndFilter]);
  const fieldOptions = useMemo(() =>
    Object.keys(ValueTypes).map(it => {
      const defaultValue = filters?.fields?.find(field => field.value === it);
      return defaultValue
        ? { ...defaultValue, label: intl.formatMessage(messages[it as keyof typeof messages]) }
        : {
          value: it,
          label: intl.formatMessage(messages[it as keyof typeof messages])
        }
    }),
    [intl, filters]
  );

  const orderByOptions = useMemo(() => [
    { value: "roomtype", label: intl.formatMessage(messages.roomTypes), isChecked: true },
    { value: "rate", label: intl.formatMessage(messages.rate) },
    { value: "mealPlans", label: intl.formatMessage(messages.mealPlans) },
    { value: "occupancy", label: intl.formatMessage(messages.occupancies) },
  ], [intl]);

  const handleToggle = () => {
    setToggleClosed(!toggleClosed);
  };

  return (
    <Container>
      <ContainerTitle>
        <ContainerTitleChevron onClick={handleToggle}>
          {intl.formatMessage(messages.sortAndFilter)}
          <Chevron toggleClosed={toggleClosed} />
        </ContainerTitleChevron>
      </ContainerTitle>

      <ContainerCollapsable role="collapsable" isVisible={toggleClosed}>
        <Container>
          <DropdownFilter
            name="roomTypes"
            title={intl.formatMessage(messages.roomTypes)}
            options={props.roomTypesOptions}
          />
          <HelperLine>
            <FilterButton /> | <ClearAllButton />
          </HelperLine>
        </Container>
        <DropdownFilter
          name="rates"
          title={intl.formatMessage(messages.conditions)}
          options={props.ratesOptions}
        />
        <DropdownFilter
          name="mealPlans"
          title={intl.formatMessage(messages.mealPlans)}
          options={props.mealPlansOptions}
        />
        <DropdownFilter
          name="occupancies"
          title={intl.formatMessage(messages.occupancies)}
          options={props.occupanciesOptions}
        />
        <VerticalLine />
        <FieldsContainer>
          <DropdownFilter
            name="fields"
            title={intl.formatMessage(messages.fields)}
            options={fieldOptions}
          />
          <HelperLine>
            <ShowOnlyMasterLinesCheck />
          </HelperLine>
        </FieldsContainer>
        <DropdownSort
          name="orderBy"
          title={intl.formatMessage(messages.orderBy)}
          options={orderByOptions}
        />
        <InventoryLineSearchBox />
      </ContainerCollapsable>
    </Container>
  );
};
